/**
 *
 *      Ask api-users to issue a password link for an address.
 *
 *      @param email user email address
 *
 */
import { apiURL } from '../../../config/baseURL';
import { gatewayFetch } from '../../../helpers/gateway';
import { readErrorMessage } from '../../../helpers/apiError';

/**
 * Firebase Auth migration: this used to call the browser Firebase SDK's
 * `sendPasswordResetEmail`, which is what #2440 is about. That was not merely
 * "one more browser-side auth call to remove" - the link it produced never came
 * back to us at all.
 *
 * `sendPasswordResetEmail(email)` takes no `actionCodeSettings` here, and the
 * production project has no custom action URL configured, so Firebase minted
 *
 *     https://nimbly-api-261017.firebaseapp.com/__/auth/action?mode=resetPassword&oobCode=...
 *
 * with no `continueUrl`. That is Firebase's own hosted OOB handler - the
 * reserved `/__/*` namespace wins over the site's `/**` rewrite to the gateway -
 * and it confirms the reset client-side against Identity Toolkit. So the user
 * never reached our set-password screen, `POST /users/confirm-password` was
 * never called, and neither `syncCredential` nor `activateProfile` ran: the
 * credential store never learned the password and the profile stayed `fresh`,
 * with `mirrorLmsActivation` never granting LMS access. It is the same
 * divergence `updateUserPassword` and `authenticateCredentials` were rewritten
 * to close, left behind on the one step that issues the link.
 *
 * `POST /v1.0/users/reset-password` mints a credential-store token, sends our
 * own template, and brings the request under `guardMailEndpoint` rate limiting
 * and the auth audit trail. It is gateway-whitelisted (`usersResetPwRe` in
 * services/gateway/auth.go), so it needs no session - which suits a screen
 * reached by an anonymous visitor.
 *
 * `reset-password` rather than `activate-user` on purpose, even for the resend
 * button that says "activation": this screen is unauthenticated and cannot know
 * the account's status, and `activate-user` answers 409 for an already-active
 * one. `confirmResetPassword` activates the profile for either purpose, so the
 * broader endpoint serves both callers and refuses nobody it should serve.
 */
/**
 * api-users' wording for a malformed address, from
 * `LoginFailureReasonMap[LoginFailureReason.INVALID_EMAIL]`.
 *
 * Matched on the message because it is the only thing that carries the
 * distinction: both outcomes are `errors.UNAUTHORIZED`, so both arrive as a 401,
 * and the error body's `enum` is only populated for a returned error rather than
 * a thrown one. If this string ever drifts the branch degrades to the
 * not-found message, which is the safer of the two to show.
 */
const INVALID_EMAIL_MESSAGE = 'Invalid User Email';

export type PasswordResetStatus = 'ok' | 'field_not_complete' | 'user_not_found' | 'rate_limited' | 'unknown_error';

export interface PasswordResetResult {
	status: PasswordResetStatus;
	errorMsg: string | null;
	errorField: string | null;
}

async function authenticatePasswordReset(email: string): Promise<PasswordResetResult> {
	if (!email) {
		return {
			status: 'field_not_complete',
			errorMsg: 'One or more fields are missing.',
			errorField: null,
		};
	}

	let response: Response;
	try {
		response = await gatewayFetch(`${apiURL}/users/reset-password`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ email }),
		});
	} catch (error) {
		// gatewayFetch resolves on a 4xx/5xx and only rejects when no response was
		// obtained at all, so this really is the network being unreachable.
		return {
			status: 'unknown_error',
			errorMsg: 'Unknown error',
			errorField: null,
		};
	}

	if (response.ok) {
		return { status: 'ok', errorMsg: null, errorField: null };
	}

	// The messages are the ones the Firebase branch returned, verbatim, so the
	// screens above behave exactly as before.
	switch (response.status) {
		case 401: {
			// `checkUser` answers 401 for two different things - an unknown
			// address and a malformed one - and only the body tells them apart.
			// The Firebase branch this replaces distinguished them too
			// (auth/user-not-found against auth/invalid-email), reporting the same
			// status with a different message, so collapsing them here would lose
			// the malformed-address feedback the login screen has always shown.
			const message = await readErrorMessage(response);

			return {
				status: 'user_not_found',
				errorMsg:
					message === INVALID_EMAIL_MESSAGE
						? 'Email address is badly formatted. Please try again.'
						: 'Email address was not found. Please try again.',
				errorField: 'forgotPass',
			};
		}
		case 429:
			// New, and it has no Firebase equivalent: api-users charges this
			// endpoint before it looks the account up, so a real user who has
			// asked several times over is told to wait rather than told their
			// address does not exist.
			return {
				status: 'rate_limited',
				errorMsg: 'Too many requests for this address. Please try again later.',
				errorField: null,
			};
		default:
			return {
				status: 'unknown_error',
				errorMsg: 'Unknown error',
				errorField: null,
			};
	}
}

export default authenticatePasswordReset;
